"use client";

import { useCallback, useMemo, useState } from "react";
import {
  applyAdvancedListQuery,
  defaultAdvancedListQueryState,
  groupAdvancedListItems,
  type AdvancedListFilterDef,
  type AdvancedListQueryState,
  type AdvancedListSortDef,
} from "./advanced-list-query.js";

export interface UseAdvancedListOptions<T> {
  items: readonly T[];
  /** Stable id for each item — required for React keys and drag reorder. */
  getId: (item: T) => string;
  /** Strings to match free-text search against. Omit to disable search. */
  searchFields?: (item: T) => Array<string | null | undefined>;
  filters?: Array<AdvancedListFilterDef<T>>;
  sorts?: Array<AdvancedListSortDef<T>>;
  /** Sort id active on first render. */
  defaultSortId?: string | null;
  /** Group already-filtered/sorted items under a key. Omit for a flat list. */
  groupBy?: (item: T) => string;
}

export interface UseAdvancedListResult<T> {
  queryState: AdvancedListQueryState;
  setSearch: (search: string) => void;
  toggleFilterValue: (filterId: string, value: string) => void;
  clearFilter: (filterId: string) => void;
  clearAllFilters: () => void;
  setSort: (sortId: string | null) => void;
  toggleSortDirection: () => void;
  /** Items after search + filters + sort, flat. */
  visibleItems: T[];
  /** Same items, grouped by `groupBy` if provided — one group `{key: "", items}` otherwise. */
  groupedItems: Array<{ key: string; items: T[] }>;
  totalCount: number;
  visibleCount: number;
  hasActiveQuery: boolean;
}

/**
 * State + derived data for AdvancedList. Split from the component so a
 * caller who wants a fully custom layout can still get the search/filter/
 * sort/group behavior without using <AdvancedList> itself.
 */
export function useAdvancedList<T>(options: UseAdvancedListOptions<T>): UseAdvancedListResult<T> {
  const [queryState, setQueryState] = useState<AdvancedListQueryState>(() => ({
    ...defaultAdvancedListQueryState(),
    sortId: options.defaultSortId ?? null,
  }));

  const setSearch = useCallback((search: string) => {
    setQueryState((prev) => ({ ...prev, search }));
  }, []);

  const toggleFilterValue = useCallback((filterId: string, value: string) => {
    setQueryState((prev) => {
      const current = prev.activeFilters[filterId] ?? [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, activeFilters: { ...prev.activeFilters, [filterId]: next } };
    });
  }, []);

  const clearFilter = useCallback((filterId: string) => {
    setQueryState((prev) => {
      const next = { ...prev.activeFilters };
      delete next[filterId];
      return { ...prev, activeFilters: next };
    });
  }, []);

  const clearAllFilters = useCallback(() => {
    setQueryState((prev) => ({ ...prev, activeFilters: {} }));
  }, []);

  const setSort = useCallback((sortId: string | null) => {
    setQueryState((prev) => ({ ...prev, sortId, sortDescending: false }));
  }, []);

  const toggleSortDirection = useCallback(() => {
    setQueryState((prev) => ({ ...prev, sortDescending: !prev.sortDescending }));
  }, []);

  const visibleItems = useMemo(
    () =>
      applyAdvancedListQuery(options.items, queryState, {
        searchFields: options.searchFields,
        filters: options.filters,
        sorts: options.sorts,
      }),
    [options.items, options.searchFields, options.filters, options.sorts, queryState]
  );

  const groupedItems = useMemo(() => {
    if (!options.groupBy) return [{ key: "", items: visibleItems }];
    return groupAdvancedListItems(visibleItems, options.groupBy);
  }, [visibleItems, options.groupBy]);

  const hasActiveQuery =
    queryState.search.trim().length > 0 ||
    Object.values(queryState.activeFilters).some((v) => v.length > 0);

  return {
    queryState,
    setSearch,
    toggleFilterValue,
    clearFilter,
    clearAllFilters,
    setSort,
    toggleSortDirection,
    visibleItems,
    groupedItems,
    totalCount: options.items.length,
    visibleCount: visibleItems.length,
    hasActiveQuery,
  };
}
